import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line,
  PieChart, Pie, Cell, CartesianGrid, Legend
} from "recharts";
import { Heart, LogOut, RefreshCcw } from "lucide-react";
import { adminStats } from "@/lib/api";
import { toast } from "sonner";

const COLORS = ["#991B1B", "#B8935A", "#3730A3", "#0F766E", "#7C2D12", "#9333EA", "#DB2777", "#134E4A"];

const Stat = ({ label, value, sub, testId }) => (
  <div data-testid={testId} className="rounded-[4px] bg-white p-6 border border-black/5 shadow-[0_10px_30px_-20px_rgba(0,0,0,0.3)]">
    <div className="text-xs uppercase tracking-widest text-[#1C1917]/50">{label}</div>
    <div className="mt-2 font-serif-elegant text-4xl text-[#1C1917]">{value}</div>
    {sub && <div className="mt-1 text-xs text-[#1C1917]/50">{sub}</div>}
  </div>
);

const Card = ({ title, children, span = "" }) => (
  <div className={`rounded-[4px] bg-white p-6 border border-black/5 shadow-[0_10px_30px_-20px_rgba(0,0,0,0.3)] ${span}`}>
    <h3 className="font-serif-elegant text-2xl text-[#1C1917]">{title}</h3>
    <div className="mt-4">{children}</div>
  </div>
);

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const token = localStorage.getItem("wh_admin_token");
    if (!token) {
      nav("/admin");
      return;
    }
    setLoading(true);
    try {
      const data = await adminStats(token);
      setStats(data);
    } catch {
      toast.error("Session expired");
      localStorage.removeItem("wh_admin_token");
      nav("/admin");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const logout = () => {
    localStorage.removeItem("wh_admin_token");
    nav("/admin");
  };

  if (loading || !stats) {
    return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center text-[#1C1917]">Loading…</div>;
  }

  const t = stats.totals;

  return (
    <div className="min-h-screen w-full bg-[#FDFBF7] text-[#1C1917]">
      <header className="max-w-7xl mx-auto flex items-center justify-between px-6 py-6">
        <Link to="/" className="flex items-center gap-2">
          <Heart size={16} className="text-[#991B1B]" fill="#991B1B" />
          <span className="font-serif-elegant text-xl">WrittenHearts</span>
          <span className="text-xs text-[#1C1917]/40 ml-2">· Admin</span>
        </Link>
        <div className="flex items-center gap-2">
          <button data-testid="admin-refresh-btn" onClick={load} className="text-sm px-4 py-2 rounded-full border border-black/10 hover:bg-black/5 flex items-center gap-2">
            <RefreshCcw size={13} /> Refresh
          </button>
          <button data-testid="admin-logout-btn" onClick={logout} className="text-sm px-4 py-2 rounded-full bg-[#1C1917] text-white hover:bg-[#3f342c] flex items-center gap-2">
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 pb-24">
        <div className="mb-8">
          <p className="uppercase tracking-[0.4em] text-xs text-[#991B1B]">Anonymous analytics</p>
          <h1 className="mt-2 font-serif-elegant text-5xl">Dashboard</h1>
          <p className="mt-2 text-[#1C1917]/60 text-sm">We never display letter content. Only aggregate metrics.</p>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat testId="kpi-letters" label="Letters Created" value={t.letters_created} />
          <Stat testId="kpi-shared" label="Letters Shared" value={t.letters_shared} />
          <Stat testId="kpi-opened" label="Letters Opened" value={t.letters_opened} sub={`Open rate ${t.open_rate}%`} />
          <Stat testId="kpi-saves" label="Saves" value={t.save_count} />
          <Stat testId="kpi-replays" label="Replays" value={t.replay_count} />
          <Stat testId="kpi-photo" label="Photo Views" value={t.photo_views} />
          <Stat testId="kpi-voice" label="Voice Plays" value={t.voice_plays} />
          <Stat testId="kpi-active" label="Daily / Monthly" value={`${t.daily_active_senders}/${t.monthly_active_senders}`} sub="active senders" />
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Stat testId="kpi-avg-writing" label="Avg Writing Time" value={`${Math.round(t.avg_writing_seconds)}s`} />
          <Stat testId="kpi-avg-reading" label="Avg Reading Time" value={`${Math.round(t.avg_reading_seconds)}s`} />
          <Stat testId="kpi-avg-length" label="Avg Letter Length" value={`${Math.round(t.avg_body_length)} ch`} />
          <Stat testId="kpi-shares" label="Total Shares" value={t.total_shares} />
        </div>

        {/* Charts */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card title="Letters per day (last 14)">
            <div style={{ height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={stats.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="letters" stroke="#991B1B" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Peak hours">
            <div style={{ height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={stats.peak_hours}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#B8935A" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Top categories">
            <List items={stats.categories} />
          </Card>

          <Card title="Top emotions">
            <List items={stats.emotions} />
          </Card>

          <Card title="Sharing channels">
            <div style={{ height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={stats.share_channels}
                    dataKey="count"
                    nameKey="channel"
                    outerRadius={90}
                    label={(x) => x.channel}
                  >
                    {stats.share_channels.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Funnel">
            <div className="space-y-3">
              {[
                ["Letters Created", stats.funnel.letters_created],
                ["Letters Shared", stats.funnel.letters_shared],
                ["Letters Opened", stats.funnel.letters_opened],
                ["Letters Saved", stats.funnel.letters_saved],
              ].map(([k, v], i) => {
                const max = stats.funnel.letters_created || 1;
                return (
                  <div key={k}>
                    <div className="flex justify-between text-sm"><span>{k}</span><span className="font-serif-elegant">{v}</span></div>
                    <div className="mt-1 h-2 bg-black/5 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(v / max) * 100}%`, background: COLORS[i] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card title="Devices">
            <List items={stats.devices} />
          </Card>

          <Card title="Browsers">
            <List items={stats.browsers} />
          </Card>

          <Card title="Languages">
            <List items={stats.languages} />
          </Card>

          <Card title="Countries">
            <List items={stats.countries} />
          </Card>
        </div>
      </main>
    </div>
  );
}

const List = ({ items }) => {
  const max = Math.max(...items.map((i) => i.count), 1);
  if (!items.length) return <div className="text-sm text-[#1C1917]/50">No data yet.</div>;
  return (
    <div className="space-y-2">
      {items.slice(0, 8).map((i, idx) => (
        <div key={i.name || i.channel} className="flex items-center gap-3">
          <div className="w-32 truncate text-sm capitalize">{(i.name || i.channel || "").replace(/_/g, " ")}</div>
          <div className="flex-1 h-2 bg-black/5 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${(i.count / max) * 100}%`, background: COLORS[idx % COLORS.length] }} />
          </div>
          <div className="w-8 text-right text-sm font-serif-elegant">{i.count}</div>
        </div>
      ))}
    </div>
  );
};
